import { useEffect, useState } from "react";
import { Send, Bell, Ban, Plus, X, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/AuthContext";
import { StatusChip, PAYMENT_STATUS, eur, ACCENT } from "./ptShared";
import { sortRows, SortSelect, MONEY_OPTIONS } from "./listSort";

/* PT Betalingen — betaalverzoeken aanmaken/versturen + herinneringen (spec #11–13) */
export default function AdminPTPayments({ prefillEmail, onPrefillUsed }) {
  const [items, setItems] = useState(null);
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ email: prefillEmail || "", kind: "single", amount: "", description: "" });
  const [showForm, setShowForm] = useState(!!prefillEmail);
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState("date_new");

  const load = () => {
    api.get("/pt/admin/payments").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
    api.get("/pt/admin/clients").then(({ data }) => setClients(data.items)).catch(() => {});
    api.get("/pt/settings").then(({ data }) => setSettings(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (prefillEmail) { setForm((f) => ({ ...f, email: prefillEmail })); setShowForm(true); onPrefillUsed?.(); } }, [prefillEmail]); // eslint-disable-line

  const packages = settings?.packages || [];
  const selPkg = packages.find((p) => p.key === form.kind);
  useEffect(() => { if (selPkg) setForm((f) => ({ ...f, amount: selPkg.price })); }, [form.kind, settings]); // eslint-disable-line

  const create = async () => {
    if (!form.email) { toast.error("Kies een klant"); return; }
    setBusy(true);
    try {
      await api.post("/pt/admin/payments", { ...form, amount: form.amount || undefined, origin_url: window.location.origin });
      toast.success("Betaalverzoek aangemaakt — status: Betaallink klaar");
      setForm({ email: "", kind: "single", amount: "", description: "" });
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Aanmaken mislukt"); }
    setBusy(false);
  };

  const act = async (p, action, msg) => {
    try {
      await api.post(`/pt/admin/payments/${p.id}/${action}`);
      toast.success(msg);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Actie mislukt"); }
  };

  const saveReminderSettings = async (patch) => {
    try {
      const { data } = await api.put("/pt/admin/settings", patch);
      setSettings(data.settings);
      toast.success("Herinneringsinstellingen opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const queued = (items || []).filter((p) => (p.reminder || {}).state === "queued");
  const label = (p) => p.status === "sent" && p.overdue ? "overdue" : p.status;
  const shown = sortRows(items || [], sort, {
    first: (p) => p.name || p.email || "",
    date: (p) => p.sent_at || p.created_at || "",
    amount: (p) => p.amount || 0,
  });

  return (
    <div data-testid="pt-payments">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="chip text-[#A3A3A3]">— BETAALVERZOEKEN</div>
        <div className="flex flex-wrap items-center gap-3">
          <SortSelect value={sort} onChange={setSort} options={MONEY_OPTIONS} testid="pt-payments-sort" className="md:w-56" />
          <button onClick={() => setShowForm(!showForm)} className="chip px-4 py-2.5 border inline-flex items-center gap-2 transition-colors" style={{ borderColor: ACCENT, color: ACCENT }} data-testid="pt-pay-new">
            {showForm ? <><X size={13}/> Sluiten</> : <><Plus size={13}/> Nieuw betaalverzoek</>}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="border border-[#F5F3F0]/15 p-5 mb-8" data-testid="pt-pay-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="chip text-[#A3A3A3]">Klant*</label>
              <select value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="editorial-input dark" disabled={!clients.length} data-testid="pt-pay-client">
                <option value="" className="bg-[#0A0A0A]">{clients.length ? "Kies klant…" : "Klanten laden…"}</option>
                {clients.map((c) => <option key={c.email} value={c.email} className="bg-[#0A0A0A]">{c.name} — {c.email}</option>)}
              </select>
            </div>
            <div><label className="chip text-[#A3A3A3]">Losse sessie of pakket*</label>
              <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))} className="editorial-input dark" data-testid="pt-pay-kind">
                {packages.map((p) => <option key={p.key} value={p.key} className="bg-[#0A0A0A]">{p.name} — {p.sessions} sessie(s) · €{p.price}</option>)}
                <option value="custom" className="bg-[#0A0A0A]">Vrij bedrag</option>
              </select>
            </div>
            <div><label className="chip text-[#A3A3A3]">Bedrag (€) — aanpasbaar</label><input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="editorial-input dark" data-testid="pt-pay-amount" /></div>
            <div><label className="chip text-[#A3A3A3]">Omschrijving (optioneel)</label><input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="editorial-input dark" data-testid="pt-pay-desc" placeholder="Bv. Personal Training — oktober" /></div>
          </div>
          <p className="chip text-[#4A4A4A] mt-3">De betaallink wordt aangemaakt via Stripe. Jij bepaalt wanneer je hem verstuurt — er gaat niets automatisch uit.</p>
          <button onClick={create} disabled={busy} className="chip mt-4 px-5 py-3" style={{ background: ACCENT, color: "#0A0A0A" }} data-testid="pt-pay-create">{busy ? "…" : "BETAALLINK AANMAKEN"}</button>
        </div>
      )}

      {/* Herinneringen wachtrij */}
      {queued.length > 0 && (
        <div className="mb-8" data-testid="pt-reminders-queue">
          <div className="chip mb-3" style={{ color: "#D4A93A" }}>— HERINNERINGEN WACHTEN OP GOEDKEURING ({queued.length})</div>
          <div className="line-t line-dark">
            {queued.map((p) => (
              <div key={p.id} className="line-b line-dark py-3 flex flex-wrap items-center gap-3">
                <span className="font-display uppercase flex-1 min-w-32">{p.name || p.email}</span>
                <span className="chip text-[#A3A3A3]">{p.label} · {eur(p.amount)}</span>
                <button onClick={() => act(p, "reminder/send", "Herinnering verstuurd")} className="chip px-3 py-2 border inline-flex items-center gap-1.5" style={{ borderColor: "#D4A93A", color: "#D4A93A" }} data-testid={`pt-reminder-send-${p.id}`}><Bell size={12}/> Verstuur herinnering</button>
                <button onClick={() => act(p, "reminder/skip", "Herinnering overgeslagen")} className="chip px-3 py-2 border border-[#F5F3F0]/20 text-[#A3A3A3]" data-testid={`pt-reminder-skip-${p.id}`}>Overslaan</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alle betaalverzoeken */}
      {items === null ? <div className="chip text-[#A3A3A3]">Laden…</div>
        : shown.length === 0 ? <div className="border border-[#F5F3F0]/15 p-8 text-center chip text-[#4A4A4A]" data-testid="pt-payments-empty">Nog geen betaalverzoeken.</div>
        : <div className="line-t line-dark">{shown.map((p) => (
            <div key={p.id} className="line-b line-dark py-3.5 flex flex-wrap items-center gap-3" data-testid={`pt-payment-${p.id}`}>
              <span className="font-display uppercase flex-1 min-w-32 truncate">{p.name || p.email}</span>
              <span className="chip text-[#A3A3A3] hidden md:inline">{p.label}</span>
              <span className="chip text-[#DDD6CB]">{eur(p.amount)}</span>
              <StatusChip map={PAYMENT_STATUS} value={label(p)} testid={`pt-payment-status-${p.id}`} />
              <span className="chip text-[#4A4A4A]">{(p.sent_at || p.created_at || "").slice(0, 10)}</span>
              <span className="flex gap-1.5">
                {(p.status === "ready" || p.status === "sent") && (
                  <button onClick={() => act(p, "send", p.status === "ready" ? "Betaallink verstuurd" : "Betaallink opnieuw verstuurd")} className="h-8 px-3 border chip inline-flex items-center gap-1 transition-colors" style={{ borderColor: `${ACCENT}88`, color: ACCENT }} data-testid={`pt-pay-send-${p.id}`}><Send size={12}/> {p.status === "ready" ? "Verstuur link" : "Verstuur opnieuw"}</button>
                )}
                {(p.status === "ready" || p.status === "sent") && (
                  <button onClick={() => { if (window.confirm("Handmatig als betaald markeren (bijv. pin/contant)?")) act(p, "mark-paid", "Gemarkeerd als betaald"); }} className="h-8 px-3 border border-[#4ADE80]/50 text-[#4ADE80] chip inline-flex items-center gap-1" data-testid={`pt-pay-markpaid-${p.id}`}><CheckCheck size={12}/> Mark as paid</button>
                )}
                {p.status === "sent" && (p.reminder || {}).state !== "sent" && (p.reminder || {}).state !== "queued" && (
                  <button onClick={() => act(p, "reminder/send", "Herinnering verstuurd")} className="h-8 px-3 border border-[#D4A93A]/50 text-[#D4A93A] chip inline-flex items-center gap-1" data-testid={`pt-pay-remind-${p.id}`}><Bell size={12}/> Herinner</button>
                )}
                {(p.status === "ready" || p.status === "sent") && (
                  <button onClick={() => { if (window.confirm("Betaallink als verlopen markeren?")) act(p, "expire", "Gemarkeerd als verlopen"); }} className="h-8 px-3 border border-[#F5F3F0]/20 text-[#A3A3A3] chip inline-flex items-center gap-1 hover:text-[#F87171]" data-testid={`pt-pay-expire-${p.id}`}><Ban size={12}/> Verlopen</button>
                )}
              </span>
            </div>
          ))}</div>}

      {/* Herinneringsinstellingen */}
      {settings && (
        <div className="border border-[#F5F3F0]/15 p-5 mt-10" data-testid="pt-reminder-settings">
          <div className="chip text-[#A3A3A3] mb-4">— BETAALHERINNERINGEN</div>
          <div className="grid md:grid-cols-3 gap-4 items-end">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!settings.reminder_enabled} onChange={(e) => saveReminderSettings({ reminder_enabled: e.target.checked })} className="w-4 h-4 accent-[#D4A93A]" data-testid="pt-rem-enabled" />
              <span className="chip text-[#DDD6CB]">Herinneringen AAN</span>
            </label>
            <div><label className="chip text-[#A3A3A3]">Herinnering na X dagen</label>
              <input type="number" min="1" max="14" value={settings.reminder_days ?? 1} onChange={(e) => saveReminderSettings({ reminder_days: parseInt(e.target.value) || 1 })} className="editorial-input dark" data-testid="pt-rem-days" />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!settings.reminder_auto} onChange={(e) => saveReminderSettings({ reminder_auto: !e.target.checked })} className="w-4 h-4 accent-[#D4A93A]" data-testid="pt-rem-manual" />
              <span className="chip text-[#DDD6CB]">Eerst handmatig goedkeuren</span>
            </label>
          </div>
          <p className="chip text-[#4A4A4A] mt-3">Standaard: 1 dag na het betaalverzoek, met jouw goedkeuring. Zet "eerst handmatig goedkeuren" uit om herinneringen automatisch te versturen.</p>
        </div>
      )}
    </div>
  );
}
